export const IS_TOUCH = navigator.maxTouchPoints > 0;

export interface TouchState {
  thrust: boolean;
  brake: boolean;
  rotateLeft: boolean;
  rotateRight: boolean;
  primaryFire: boolean;
  secondaryFire: boolean;
}

export const touchState: TouchState = {
  thrust: false,
  brake: false,
  rotateLeft: false,
  rotateRight: false,
  primaryFire: false,
  secondaryFire: false,
};

/** HUD panel width in CSS pixels. 420 on desktop, smaller on touch to maximise game area. */
export function getHudWidth(screenW: number): number {
  if (!IS_TOUCH) return 420;
  return screenW >= 1024 ? 320 : 240;
}

let overlay: HTMLElement | null = null;

export function unmountTouchControls(): void {
  overlay?.remove();
  overlay = null;
  touchState.thrust = touchState.brake = touchState.rotateLeft =
    touchState.rotateRight = touchState.primaryFire = touchState.secondaryFire = false;
}

export function mountTouchControls(hudW: number): void {
  if (overlay) return;

  overlay = document.createElement('div');
  overlay.id = 'touch-overlay';

  // Button size and gap — finger-sized
  const BTN = 58;
  const GAP =  4;
  const STEP = BTN + GAP;

  // Fire buttons sit to the left of the HUD panel
  const fireBtnRight = hudW + 28;
  const secBtnRight  = hudW + 36;

  overlay.innerHTML = `
    <style>
      #touch-overlay {
        position: fixed;
        inset: 0;
        z-index: 500;
        pointer-events: none;
        user-select: none;
        -webkit-user-select: none;
      }

      /* ── D-pad cluster ─────────────────────────────────────────────── */
      #tc-dpad {
        position: absolute;
        bottom: calc(20px + env(safe-area-inset-bottom, 0px));
        left:   calc(20px + env(safe-area-inset-left,   0px));
        width:  ${STEP * 3 - GAP}px;
        height: ${STEP * 2 - GAP}px;
        pointer-events: none;
      }
      .tc-dpad-btn {
        position: absolute;
        width:  ${BTN}px;
        height: ${BTN}px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 22px;
        color: rgba(100,170,255,0.75);
        background: rgba(0,10,30,0.55);
        border: 2px solid rgba(68,136,255,0.40);
        border-radius: 8px;
        pointer-events: all;
        touch-action: none;
        -webkit-tap-highlight-color: transparent;
        cursor: pointer;
        transition: background 60ms, border-color 60ms, color 60ms;
        box-sizing: border-box;
      }
      .tc-dpad-btn.tc-active {
        background: rgba(68,136,255,0.35);
        border-color: rgba(136,187,255,0.80);
        color: #ffffff;
        box-shadow: 0 0 14px rgba(68,136,255,0.50);
      }
      /* Inverted-T layout: UP centre-top, LEFT/DOWN/RIGHT bottom row */
      #tc-up    { left: ${STEP}px;      top: 0; }
      #tc-left  { left: 0;              top: ${STEP}px; }
      #tc-down  { left: ${STEP}px;      top: ${STEP}px; }
      #tc-right { left: ${STEP * 2}px;  top: ${STEP}px; }

      /* ── Fire buttons ───────────────────────────────────────────────── */
      #tc-fire-btn, #tc-sec-btn {
        position: absolute;
        border-radius: 50%;
        border: none;
        pointer-events: all;
        touch-action: none;
        -webkit-tap-highlight-color: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'KenvectorFuture', monospace;
        letter-spacing: .14em;
        transition: background 80ms, box-shadow 80ms;
      }
      #tc-fire-btn {
        width: 80px; height: 80px;
        font-size: 10px;
        color: #ffaaaa;
        background: rgba(160,20,20,0.50);
        border: 2px solid rgba(255,90,90,0.65);
        box-shadow: 0 0 18px rgba(255,50,50,0.40);
        bottom: calc(28px + env(safe-area-inset-bottom, 0px));
        right: ${fireBtnRight}px;
      }
      #tc-fire-btn.tc-active {
        background: rgba(255,50,50,0.65);
        box-shadow: 0 0 30px rgba(255,50,50,0.70);
      }
      #tc-sec-btn {
        width: 62px; height: 62px;
        font-size: 9px;
        color: #aaffee;
        background: rgba(10,100,100,0.50);
        border: 2px solid rgba(0,200,180,0.55);
        box-shadow: 0 0 14px rgba(0,200,180,0.30);
        bottom: calc(124px + env(safe-area-inset-bottom, 0px));
        right: ${secBtnRight}px;
      }
      #tc-sec-btn.tc-active {
        background: rgba(0,200,180,0.60);
        box-shadow: 0 0 24px rgba(0,200,180,0.65);
      }
    </style>

    <div id="tc-dpad">
      <div id="tc-up"    class="tc-dpad-btn">&#x25B2;</div>
      <div id="tc-left"  class="tc-dpad-btn">&#x25C4;</div>
      <div id="tc-down"  class="tc-dpad-btn">&#x25BC;</div>
      <div id="tc-right" class="tc-dpad-btn">&#x25BA;</div>
    </div>

    <div id="tc-fire-btn">FIRE</div>
    <div id="tc-sec-btn">SEC</div>
  `;

  document.body.appendChild(overlay);

  // ── D-pad wiring ─────────────────────────────────────────────────────────
  wireDpad('tc-up',    () => { touchState.thrust      = true;  }, () => { touchState.thrust      = false; });
  wireDpad('tc-down',  () => { touchState.brake       = true;  }, () => { touchState.brake       = false; });
  wireDpad('tc-left',  () => { touchState.rotateLeft  = true;  }, () => { touchState.rotateLeft  = false; });
  wireDpad('tc-right', () => { touchState.rotateRight = true;  }, () => { touchState.rotateRight = false; });

  function wireDpad(id: string, down: () => void, up: () => void) {
    const el = document.getElementById(id)!;
    el.addEventListener('touchstart', (e) => {
      e.preventDefault(); down(); el.classList.add('tc-active');
    }, { passive: false });
    el.addEventListener('touchend', (e) => {
      e.preventDefault(); up(); el.classList.remove('tc-active');
    }, { passive: false });
    el.addEventListener('touchcancel', (e) => {
      e.preventDefault(); up(); el.classList.remove('tc-active');
    }, { passive: false });
  }

  // ── Fire / secondary buttons ─────────────────────────────────────────────
  wireBtn('tc-fire-btn',
    () => { touchState.primaryFire = true;  },
    () => { touchState.primaryFire = false; },
  );
  wireBtn('tc-sec-btn',
    () => { touchState.secondaryFire = true;  },
    () => { touchState.secondaryFire = false; },
  );

  function wireBtn(id: string, down: () => void, up: () => void) {
    const el = document.getElementById(id)!;
    el.addEventListener('touchstart', (e) => {
      e.preventDefault(); down(); el.classList.add('tc-active');
    }, { passive: false });
    el.addEventListener('touchend', (e) => {
      e.preventDefault(); up(); el.classList.remove('tc-active');
    }, { passive: false });
    el.addEventListener('touchcancel', (e) => {
      e.preventDefault(); up(); el.classList.remove('tc-active');
    }, { passive: false });
  }
}
